import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { T, useT } from "@/lib/translation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getBillingStatus,
  getSubscriptionDetails,
  cancelSubscription,
  startBillingPortal,
  upgradeToYearly,
} from "@/api/client";
import { SubscribeDialog } from "@/components/billing/SubscribeDialog";
import { getIntlLocale, currencyDisplayFor } from "@/lib/locale";
import { getPlanPrices } from "@/lib/pricing";

// Trans' own key typing resolves to `never` with this project's non-strict TS
// config, so keys are checked here against the English files and cast once.

// Stripe statuses of a subscription that has ended. The backend keeps the Stripe id after it
// ends, so /billing/subscription still reports one, but the user has nothing left to manage.
const ENDED_SUBSCRIPTION_STATUSES = ["canceled", "incomplete_expired"];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(getIntlLocale(undefined), {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat(getIntlLocale(undefined), {
    style: "currency",
    currency: currency.toUpperCase(),
    currencyDisplay: currencyDisplayFor(
      getIntlLocale(undefined),
      currency.toUpperCase(),
    ),
  }).format(amount / 100);
}

export default function SubscriptionCard() {
  const t = useT();
  const queryClient = useQueryClient();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ["billing-info-status"],
    queryFn: getBillingStatus,
  });

  const { data: sub, isLoading: subLoading } = useQuery({
    queryKey: ["subscription-details"],
    queryFn: getSubscriptionDetails,
  });

  const isLoading = statusLoading || subLoading;

  const refetch = () => {
    queryClient
      .invalidateQueries({ queryKey: ["subscription-details"] })
      .catch(() => {});
    queryClient
      .invalidateQueries({ queryKey: ["billing-info-status"] })
      .catch(() => {});
  };

  const handleManageBilling = async () => {
    setIsOpeningPortal(true);
    try {
      const url = await startBillingPortal();
      window.location.href = url;
    } catch {
      toast.error(t("settings:subscription.toast.portalFailed"), {
        description: t("settings:subscription.toast.tryAgain"),
      });
      setIsOpeningPortal(false);
    }
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await cancelSubscription();
      toast.success(t("settings:subscription.toast.cancelled"), {
        description: status?.free_tier_enabled
          ? t("settings:subscription.toast.cancelledFreeDescription")
          : t("settings:subscription.toast.cancelledDescription"),
      });
      setShowCancelDialog(false);
      refetch();
    } catch {
      toast.error(t("settings:subscription.toast.cancelFailed"), {
        description: t("settings:subscription.toast.tryAgain"),
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    try {
      await upgradeToYearly();
      toast.success(t("settings:subscription.toast.yearlyScheduled"), {
        description: t(
          "settings:subscription.toast.yearlyScheduledDescription",
        ),
      });
      setShowUpgradeDialog(false);
      refetch();
    } catch {
      toast.error(t("settings:subscription.toast.upgradeFailed"), {
        description: t("settings:subscription.toast.tryAgain"),
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  if (isLoading || !status) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("settings:subscription.title")}</CardTitle>
          <CardDescription>
            {t("settings:subscription.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t("common:status.loading")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const inGracePeriod = new Date(status.trial_ends_at).getTime() > Date.now();
  // With the free tier on, a user without a subscription after the trial is on the free plan,
  // not locked out, and cancelling a subscription means switching to that plan.
  const freeTierEnabled = status.free_tier_enabled === true;
  const statusBadge = status.is_ff
    ? {
        label: t("settings:subscription.status.active"),
        variant: "gain" as const,
      }
    : status.subscription_status === "active"
      ? {
          label: t("settings:subscription.status.active"),
          variant: "gain" as const,
        }
      : inGracePeriod || status.subscription_status === "trialing"
        ? {
            label: t("settings:subscription.status.trial"),
            variant: "secondary" as const,
          }
        : freeTierEnabled
          ? {
              label: t("settings:subscription.status.free"),
              variant: "secondary" as const,
            }
          : {
              label: t("settings:subscription.status.inactive"),
              variant: "loss" as const,
            };

  const typeBadge = status.is_ff
    ? t("settings:subscription.type.friendsFamily")
    : status.is_founding_member
      ? t("settings:subscription.type.founder")
      : t("settings:subscription.type.standard");

  const hasSubscription =
    sub?.has_subscription === true &&
    !ENDED_SUBSCRIPTION_STATUSES.includes(sub.status);
  // There's no live price to quote from Stripe until the user actually has a subscription,
  // so the figures come from the same table the checkout screens use.
  const price = getPlanPrices(status.is_founding_member);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col space-y-1.5">
            <CardTitle>{t("settings:subscription.title")}</CardTitle>
            <CardDescription>
              {t("settings:subscription.description")}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
            <Badge variant="outline">{typeBadge}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.is_ff ? (
          <p className="text-sm text-muted-foreground">
            {t("settings:subscription.friendsFamilyMessage")}
          </p>
        ) : (
          <>
            {!hasSubscription && (
              <>
                <p className="text-sm text-muted-foreground">
                  {inGracePeriod ? (
                    <T
                      k="settings:subscription.trialActive"
                      values={{ date: formatDate(status.trial_ends_at) }}
                      components={{ strong: <strong /> }}
                    />
                  ) : freeTierEnabled ? (
                    t("settings:subscription.freePlan")
                  ) : (
                    <T
                      k="settings:subscription.trialEnded"
                      values={{ date: formatDate(status.trial_ends_at) }}
                      components={{ strong: <strong /> }}
                    />
                  )}
                </p>
                <div className="flex justify-end">
                  <SubscribeDialog>
                    <Button type="button">
                      {t("settings:subscription.subscribe")}
                    </Button>
                  </SubscribeDialog>
                </div>
              </>
            )}
            {status.is_founding_member && hasSubscription && (
              <p className="text-sm text-muted-foreground">
                {inGracePeriod && (
                  <>
                    <T
                      k="settings:subscription.foundingTrialEnds"
                      values={{ date: formatDate(status.trial_ends_at) }}
                      components={{ strong: <strong /> }}
                    />{" "}
                  </>
                )}
                {t("settings:subscription.foundingRateLocked")}
              </p>
            )}

            {hasSubscription && sub ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">
                      {formatAmount(sub.amount, sub.currency)} /{" "}
                      {t(`settings:subscription.interval.${sub.interval}`)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {sub.cancel_at_period_end
                        ? t("settings:subscription.cancelledAccessEnds", {
                            date: formatDate(sub.current_period_end),
                          })
                        : t("settings:subscription.renewsOn", {
                            date: formatDate(sub.current_period_end),
                          })}
                    </p>
                  </div>
                </div>

                <div
                  className={`grid gap-2 ${
                    sub.cancel_at_period_end
                      ? "grid-cols-1"
                      : sub.interval === "month"
                        ? "grid-cols-3"
                        : "grid-cols-2"
                  }`}
                >
                  {sub.interval === "month" && !sub.cancel_at_period_end && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowUpgradeDialog(true)}
                    >
                      {t("settings:subscription.upgradeToYearly")}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleManageBilling}
                    disabled={isOpeningPortal}
                  >
                    {isOpeningPortal
                      ? t("settings:subscription.opening")
                      : t("settings:subscription.manageBilling")}
                  </Button>
                  {!sub.cancel_at_period_end && (
                    <Button
                      type="button"
                      variant="outline"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                      onClick={() => setShowCancelDialog(true)}
                    >
                      {freeTierEnabled
                        ? t("settings:subscription.switchToFree")
                        : t("settings:subscription.unsubscribe")}
                    </Button>
                  )}
                </div>
              </>
            ) : null}
          </>
        )}
      </CardContent>

      {hasSubscription && sub && sub.has_subscription && (
        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {freeTierEnabled
                  ? t("settings:subscription.cancelDialog.switchTitle")
                  : t("settings:subscription.cancelDialog.unsubscribeTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                {freeTierEnabled ? (
                  <span className="block">
                    <T
                      k="settings:subscription.cancelDialog.freeBody"
                      values={{ date: formatDate(sub.current_period_end) }}
                      components={{ strong: <strong /> }}
                    />
                  </span>
                ) : (
                  <span className="block">
                    <T
                      k="settings:subscription.cancelDialog.paidBody"
                      values={{ date: formatDate(sub.current_period_end) }}
                      components={{ strong: <strong /> }}
                    />
                  </span>
                )}
                {status.is_founding_member && (
                  <span className="block">
                    {t("settings:subscription.cancelDialog.founderNote")}
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isCancelling}>
                {t("common:actions.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancel}
                disabled={isCancelling}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isCancelling
                  ? freeTierEnabled
                    ? t("settings:subscription.cancelDialog.switching")
                    : t("settings:subscription.cancelDialog.unsubscribing")
                  : t("common:actions.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {hasSubscription && sub && sub.has_subscription && (
        <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("settings:subscription.upgradeDialog.title")}
              </DialogTitle>
              <DialogDescription>
                <T
                  k={
                    price.onSale
                      ? "settings:subscription.upgradeDialog.bodySale"
                      : "settings:subscription.upgradeDialog.body"
                  }
                  values={{
                    date: formatDate(sub.current_period_end),
                    price: price.yearly,
                    regular: price.yearlyList,
                    percent: price.onSale
                      ? price.salePercentOff
                      : price.yearlySavingsPercent,
                  }}
                  components={{ strong: <strong /> }}
                />
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowUpgradeDialog(false)}
                disabled={isUpgrading}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button
                type="button"
                onClick={handleUpgrade}
                disabled={isUpgrading}
              >
                {isUpgrading
                  ? t("settings:subscription.upgradeDialog.upgrading")
                  : t("settings:subscription.upgradeDialog.confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
